import { useState } from "react";
import { motion } from "framer-motion";
import { Phone, Mail, MapPin, Send, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, BUSINESS } from "@/lib/api";

const SERVICES = [
  "Pesticide Application",
  "Herbicide Spraying",
  "Fungicide Treatment",
  "Liquid Fertilizer",
  "Cover Crop Seeding",
  "Crop Scouting & Mapping",
  "Multiple / Not Sure",
];

const CROPS = [
  "Avocado",
  "Mango / Tropical Fruit",
  "Tomato / Squash",
  "Sweet Corn",
  "Sugarcane",
  "Citrus / Lime",
  "Beans / Okra",
  "Ornamental Nursery",
  "Other",
];

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    farm_name: "",
    phone: "",
    email: "",
    acreage: "",
    crop_type: "",
    service: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.email) {
      toast.error("Please fill name, phone and email.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/leads", form);
      setSent(true);
      toast.success("Thanks! We'll be in touch within 24 hours.");
      setForm({ name: "", farm_name: "", phone: "", email: "", acreage: "", crop_type: "", service: "", message: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map((d) => d.msg).join(" · ")
        : typeof detail === "string"
        ? detail
        : "Could not submit. Please call us directly.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="relative py-24 md:py-32 bg-muted/30">
      <div className="max-w-7xl mx-auto px-5 md:px-8 grid lg:grid-cols-12 gap-12">
        <motion.div
          initial={{ opacity: 0, x: -16 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="lg:col-span-5"
        >
          <div className="text-xs font-semibold tracking-[0.2em] uppercase text-accent mb-4">Get in touch</div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-primary font-bold leading-tight">
            Get a free quote in <span className="text-accent">under 24 hours.</span>
          </h2>
          <p className="mt-5 text-base text-muted-foreground">
            Tell us a bit about your field — we'll come back with pricing, available slots and the right chemical
            program for your crop.
          </p>

          <div className="mt-10 space-y-5">
            <a href={`tel:${BUSINESS.phoneRaw}`} data-testid="contact-phone" data-conversion="phone-call" className="track-phone-click group flex items-center gap-4 p-5 rounded-2xl bg-card border border-border hover:border-accent transition-all shadow-card hover:shadow-card-hover">
              <div className="h-12 w-12 rounded-2xl bg-accent/10 text-accent flex items-center justify-center">
                <Phone className="h-5 w-5" />
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-muted-foreground">Call us directly</div>
                <div className="font-display text-xl font-bold text-primary group-hover:text-accent transition-colors">{BUSINESS.phone}</div>
              </div>
            </a>
            <a href={`mailto:${BUSINESS.email}`} data-testid="contact-email" className="group flex items-center gap-4 p-5 rounded-2xl bg-card border border-border hover:border-accent transition-all shadow-card hover:shadow-card-hover">
              <div className="h-12 w-12 rounded-2xl bg-accent/10 text-accent flex items-center justify-center">
                <Mail className="h-5 w-5" />
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-muted-foreground">Email</div>
                <div className="font-display text-base font-semibold text-primary group-hover:text-accent">{BUSINESS.email}</div>
              </div>
            </a>
            <div data-testid="contact-address" className="flex items-center gap-4 p-5 rounded-2xl bg-card border border-border shadow-card">
              <div className="h-12 w-12 rounded-2xl bg-accent/10 text-accent flex items-center justify-center">
                <MapPin className="h-5 w-5" />
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-muted-foreground">Based in</div>
                <div className="font-display text-base font-semibold text-primary">{BUSINESS.address}</div>
                <div className="text-xs text-muted-foreground mt-0.5">{BUSINESS.area}</div>
              </div>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: 16 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="lg:col-span-7"
        >
          <div className="bg-card border border-border rounded-3xl p-7 md:p-10 shadow-card">
            {sent ? (
              <div data-testid="contact-success" className="flex flex-col items-center text-center py-12">
                <div className="h-16 w-16 rounded-2xl bg-accent/10 text-accent flex items-center justify-center mb-5">
                  <CheckCircle2 className="h-8 w-8" />
                </div>
                <h3 className="font-display text-2xl md:text-3xl font-bold text-primary">Quote request received</h3>
                <p className="mt-3 text-muted-foreground max-w-md">
                  Thanks! One of our team members will call or email you within 24 hours with pricing and availability.
                </p>
                <Button onClick={() => setSent(false)} variant="outline" className="mt-7 rounded-full" data-testid="contact-send-another">Send another request</Button>
              </div>
            ) : (
              <form onSubmit={submit} data-testid="contact-form" className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div className="md:col-span-1">
                  <Label htmlFor="name" className="text-xs font-semibold uppercase tracking-wider text-primary/70">Your name *</Label>
                  <Input id="name" data-testid="input-name" value={form.name} onChange={set("name")} placeholder="Jane Garcia" className="mt-2 h-12 rounded-xl bg-background" />
                </div>
                <div>
                  <Label htmlFor="farm" className="text-xs font-semibold uppercase tracking-wider text-primary/70">Farm name</Label>
                  <Input id="farm" data-testid="input-farm" value={form.farm_name} onChange={set("farm_name")} placeholder="Redland Groves" className="mt-2 h-12 rounded-xl bg-background" />
                </div>
                <div>
                  <Label htmlFor="phone" className="text-xs font-semibold uppercase tracking-wider text-primary/70">Phone *</Label>
                  <Input id="phone" data-testid="input-phone" type="tel" value={form.phone} onChange={set("phone")} placeholder="(786) 555-0123" className="mt-2 h-12 rounded-xl bg-background" />
                </div>
                <div>
                  <Label htmlFor="email" className="text-xs font-semibold uppercase tracking-wider text-primary/70">Email *</Label>
                  <Input id="email" data-testid="input-email" type="email" value={form.email} onChange={set("email")} placeholder="you@farm.com" className="mt-2 h-12 rounded-xl bg-background" />
                </div>
                <div>
                  <Label htmlFor="acreage" className="text-xs font-semibold uppercase tracking-wider text-primary/70">Acreage</Label>
                  <Input id="acreage" data-testid="input-acreage" value={form.acreage} onChange={set("acreage")} placeholder="e.g. 40 acres" className="mt-2 h-12 rounded-xl bg-background" />
                </div>
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-primary/70">Crop type</Label>
                  <Select value={form.crop_type} onValueChange={set("crop_type")}>
                    <SelectTrigger data-testid="select-crop" className="mt-2 h-12 rounded-xl bg-background">
                      <SelectValue placeholder="Select a crop" />
                    </SelectTrigger>
                    <SelectContent>
                      {CROPS.map((c) => (
                        <SelectItem key={c} value={c}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="md:col-span-2">
                  <Label className="text-xs font-semibold uppercase tracking-wider text-primary/70">Service needed</Label>
                  <Select value={form.service} onValueChange={set("service")}>
                    <SelectTrigger data-testid="select-service" className="mt-2 h-12 rounded-xl bg-background">
                      <SelectValue placeholder="Choose a service" />
                    </SelectTrigger>
                    <SelectContent>
                      {SERVICES.map((s) => (
                        <SelectItem key={s} value={s}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="md:col-span-2">
                  <Label htmlFor="msg" className="text-xs font-semibold uppercase tracking-wider text-primary/70">Tell us about your field</Label>
                  <Textarea id="msg" data-testid="input-message" value={form.message} onChange={set("message")} placeholder="Acreage, location, pest pressure, timing..." rows={4} className="mt-2 rounded-xl bg-background" />
                </div>
                <div className="md:col-span-2 flex flex-col sm:flex-row gap-3 items-center justify-between pt-2">
                  <p className="text-xs text-muted-foreground">By submitting you agree to be contacted by AeroAcres about your project.</p>
                  <Button type="submit" disabled={loading} data-testid="contact-submit-button" className="rounded-full bg-primary hover:bg-secondary text-primary-foreground h-12 px-7 font-semibold">
                    {loading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Sending...</> : <>Send request <Send className="h-4 w-4 ml-2" /></>}
                  </Button>
                </div>
              </form>
            )}
          </div>
        </motion.div>
      </div>
    </section>
  );
}
